import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { CompanyStageStatus, StageStatusResurfaceMode } from '@/lib/types';
import { StageStatusFollowUpDialog } from '@/components/companies/StageStatusFollowUpDialog';
import { needsFollowUpDialog } from '@/lib/stage-status-follow-up';

/*
 * useStageStatusChange — asks for whatever a company sub-stage needs before the
 * caller PATCHes it (spec 2026-10-23).
 *
 *   const { request, dialog } = useStageStatusChange();
 *   const extra = await request({ companyName, status, currentStatus, currentFollowUpOn });
 *   if (extra === null) return;                 // cancelled: change nothing
 *   await api.updateCompany(id, { stageStatusId: status?.id ?? null, ...extra });
 *
 * - Resolves `{}` at once when the status needs no input (`needsFollowUpDialog`,
 *   driven by flags), including a cleared status (`null`).
 * - Resolves `null` on cancel, when a newer request replaces a pending one, and
 *   when the host unmounts.
 * - Only the keys the status asks for are present: `followUpOn` for a resurface
 *   status, `stageStatusReason` when a reason is required.
 *
 * Render `dialog` once per page (page-level modal rule). The dialog closes on
 * Confirm; the caller's save and any server refusal belong to the caller.
 *
 * `.tsx`, not the spec's `.ts`: the hook returns JSX.
 */

export type StageStatusChangeExtra = { followUpOn?: string | null; stageStatusReason?: string };

export interface StageStatusChangeRequest {
  companyName: string;
  status: CompanyStageStatus | null;
  currentFollowUpOn?: string | null;
  /**
   * The company's status BEFORE this change. `currentFollowUpOn` pre-fills the
   * dialog only when this has the same resurfaceMode as `status` (and the date
   * is still in the future). Omitted = unknown: the dialog computes a fresh
   * default (next 1 March for next_cycle, empty for date).
   */
  currentStatus?: CompanyStageStatus | null;
}

interface Pending {
  seq: number;
  companyName: string;
  status: CompanyStageStatus;
  currentFollowUpOn: string | null;
  currentResurfaceMode: StageStatusResurfaceMode | null | undefined;
}

export function useStageStatusChange(): {
  request(input: StageStatusChangeRequest): Promise<StageStatusChangeExtra | null>;
  dialog: ReactNode;
} {
  const [pending, setPending] = useState<Pending | null>(null);
  const seq = useRef(0);
  const resolver = useRef<((value: StageStatusChangeExtra | null) => void) | null>(null);

  const settle = useCallback((value: StageStatusChangeExtra | null) => {
    const resolve = resolver.current;
    resolver.current = null;
    setPending(null);
    resolve?.(value);
  }, []);

  useEffect(
    () => () => {
      const resolve = resolver.current;
      resolver.current = null;
      resolve?.(null);
    },
    [],
  );

  const request = useCallback(
    (input: StageStatusChangeRequest): Promise<StageStatusChangeExtra | null> => {
      const { status } = input;
      if (status === null || !needsFollowUpDialog(status)) return Promise.resolve({});
      // A second request supersedes the first; the first caller sees a cancel.
      const previous = resolver.current;
      resolver.current = null;
      previous?.(null);
      return new Promise<StageStatusChangeExtra | null>((resolve) => {
        resolver.current = resolve;
        seq.current += 1;
        setPending({
          seq: seq.current,
          companyName: input.companyName,
          status,
          currentFollowUpOn: input.currentFollowUpOn ?? null,
          // undefined (not passed) stays unknown; a null status has no mode.
          currentResurfaceMode:
            input.currentStatus === undefined
              ? undefined
              : (input.currentStatus?.resurfaceMode ?? null),
        });
      });
    },
    [],
  );

  const dialog = pending ? (
    <StageStatusFollowUpDialog
      key={pending.seq}
      open
      companyName={pending.companyName}
      status={pending.status}
      initialFollowUpOn={pending.currentFollowUpOn}
      currentResurfaceMode={pending.currentResurfaceMode}
      onCancel={() => settle(null)}
      onConfirm={({ followUpOn, stageStatusReason }) => {
        const extra: StageStatusChangeExtra = {};
        if (pending.status.resurfaceMode != null) extra.followUpOn = followUpOn;
        if (pending.status.requiresReason === true && stageStatusReason !== null)
          extra.stageStatusReason = stageStatusReason;
        settle(extra);
      }}
    />
  ) : null;

  return { request, dialog };
}
